// StickerPicker — 메신저 스티커 피커(J2b): iframe **밖**(부모 레이어)에서 게이트웨이가 그리는 패널.
//
// 설계: `patch_mm_sticker_picker_j2b_picker_ui.md` §2-1 (버튼 주입·전송 유틸은 J2a `utils/mmSticker`).
//
// 왜 부모가 그리는가: 선례 `ReadReceiptModal` 과 같다 — MM DOM 안에 패널을 만들면 MM 이 버전업할
// 때마다 같이 깨진다. 버튼만 iframe 안에 주입하고(J2a), 패널은 여기서 그린다.
//
// 🔴 모듈 최상위에서 `stickerAPI` 를 구조분해·호출하지 않는다 — EmbedPage 를 끌고 오는 이웃
//    시험들의 client 목에 `stickerAPI` 가 없다(없어도 그 화면이 죽으면 안 된다).
// 🔴 썸네일은 **팩당 요청 1번**이다(GET /api/stickers/thumbs/{pack} = 128px data URL 묶음 —
//    PNG 첫 프레임, 움직이는 GIF·WebP 는 같은 형식의 움직이는 축소본).
//    예전엔 타일마다 618px 원본을 받았는데, PROD nginx 의 `/api/` 가 apizone `rate=5r/s`(사무실 NAT
//    공인 IP 만 면제) 라 시작 간격 250ms 를 둘 수밖에 없었고 38장이면 그것만으로 9.5초였다.
//    요청 수 자체를 줄여야 풀리는 문제라 간격·동시개수·백오프 큐를 통째로 걷어냈다.
// 🔴 원본(stickerAPI.image)은 **클릭한 한 장**만 받는다 — 입력창에 올릴 Blob 이 필요한 그때뿐이다.
// 🔴 클릭은 게시가 아니다 — 입력창 위 첨부 대기열에 올린다(stageSticker · 다시 고르면 앞 스티커와 교체).
//    게시는 사용자가 엔터/전송으로 한다. 이미지 받는 동안의 «다른 클릭»은 무시한다(이중 처리 방지).
import { useEffect, useRef, useState } from 'react';
import { stickerAPI } from '../api/client';
import { BUTTON_ID, StickerSendError, TEXTBOX_SEL, parseMmChannelPath, stageSticker } from '../utils/mmSticker';
import { RECENT_TAB_KEY, pushRecent, readLastTab, readRecent, resolveRecent, writeLastTab } from '../utils/stickerRecent';
import useIsMobile from '../hooks/useIsMobile';
import { scaleOf } from '../lib/floatingBounds';

// 서버가 주는 썸네일 접두(PNG · 움직이는 GIF·WebP). 🔴 허용 목록이다 — 이 접두들이 아닌 값
//    (javascript:·data:image/svg+xml·data:text/html 등)은 src 에 넣지 않는다(회색 칸으로 둔다).
const THUMB_PREFIXES = ['data:image/png;base64,', 'data:image/gif;base64,', 'data:image/webp;base64,'];

// «최근» 탭의 키. 🔴 팩 이름 규칙(^[a-z0-9_-]{1,64}$ — backend/routers/stickers.py)에 ':' 가
//    없으므로 어떤 팩 이름과도 충돌하지 않는다('__recent__' 는 그 규칙을 통과해 충돌할 수 있었다).
//    값은 유틸의 것을 쓴다(마지막 탭 저장값과 두 벌이 되지 않게).
const RECENT_TAB = RECENT_TAB_KEY;
const MSG_RECENT = '최근';
// 최근이 비었을 때 상태를 같은 참조로 유지해 쓸데없는 재렌더를 만들지 않는다.
const NO_RECENT = [];

const PANEL_W = 336;
const PANEL_H = 380;
const TILE = 64;
const EDGE = 8;

const MSG_EMPTY = '아직 등록된 스티커가 없습니다.';
const MSG_MANIFEST_ERR = '스티커 목록을 불러오지 못했습니다.';
const MSG_RETRY = '다시 시도';
const MSG_CHANNEL_CHANGED = '대화방이 바뀌었습니다 — 다시 열어 주세요.';
const MSG_NO_FRAME = '메신저 화면을 찾지 못했습니다 — 메신저를 새로고침해 주세요.';
const MSG_IMAGE_FAILED = '스티커 이미지를 불러오지 못했습니다.';
const MSG_STAGE_FAILED = '스티커를 입력창에 올리지 못했습니다.';

// 🔴 라이선스 링크는 **아는 문자열만** deed 로 건다(CC BY-SA §3(a)(1)(C) 의 라이선스 링크 요건).
//    manifest 에는 license_url 이 없다(backend/routers/stickers.py 의 팩 필드) — 여기 맵이 전부다.
const LICENSE_DEEDS = {
  'CC BY-SA 4.0': 'https://creativecommons.org/licenses/by-sa/4.0/',
  'CC0 1.0': 'https://creativecommons.org/publicdomain/zero/1.0/',
};

// manifest·썸네일 묶음은 «성공한 결과만» 모듈에 캐시한다 — 오류를 캐시하면 다시 열어도
// 영영 빈 목록/빈 그리드가 된다.
let manifestCache = null;
let thumbsCache = {};    // pack -> { <id>: dataURL }

// 🔴 시험 전용 — 모듈 전역 캐시만 비운다(제품 코드에서는 부르지 않는다).
//    왜 이런 구멍을 두는가: 시험이 캐시를 지우려고 `jest.resetModules()` 로 이 모듈을 다시 읽으면
//    컴포넌트는 «두 번째 React 복사본»의 훅을 잡고 @testing-library 는 첫 React 로 렌더해서
//    useState 가 null 을 돌려준다(전 시험 실패). 모듈은 한 번만 읽고, 캐시만 여기서 비운다.
export function _resetManifestCacheForTest() {
  manifestCache = null;
  thumbsCache = {};
}

const tileKey = (pack, id) => String(pack) + '/' + String(id);

function statusOf(err) {
  try {
    return Number(err && err.response && err.response.status) || 0;
  } catch (e) {
    return 0;
  }
}

// http(s) 가 아닌 링크는 링크로 만들지 않는다 — `javascript:` 는 게이트웨이 도메인의 XSS 가 된다.
function safeHttpUrl(value) {
  const url = String(value === undefined || value === null ? '' : value);
  return (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) ? url : '';
}

// 🔴 방 비교는 **값**으로 한다 — parseMmChannelPath 는 부를 때마다 새 객체를 돌려주므로
//    참조 비교(`!==`)는 «항상 다르다» 가 되어 전송이 통째로 막힌다.
const CHANNEL_FIELDS = ['kind', 'team', 'name', 'username', 'email', 'userId'];
export function sameChannel(a, b) {
  if (!a || !b) return !a && !b;   // null 끼리는 같다
  for (let i = 0; i < CHANNEL_FIELDS.length; i += 1) {
    const field = CHANNEL_FIELDS[i];
    const left = a[field] === undefined || a[field] === null ? '' : String(a[field]);
    const right = b[field] === undefined || b[field] === null ? '' : String(b[field]);
    if (left !== right) return false;
  }
  return true;
}

// win.location getter 는 교차 출처·이동 중에 던진다 — 던지면 «모르겠다(null)» 로 본다.
function readWinChannel(win) {
  try {
    const pathname = win && win.location ? win.location.pathname : '';
    return parseMmChannelPath(pathname);
  } catch (e) {
    return null;
  }
}

// 데스크톱이면 메신저 입력창에 포커스를 돌려준다 — 올린 뒤 바로 엔터로 보낼 수 있게.
// 🔴 터치 기기((pointer: coarse))는 옮기지 않는다 — 소프트 키보드가 튀어 오른다(선례 mmExcelPaste isCoarsePointer).
function focusTextbox(doc) {
  try {
    const view = (doc && doc.defaultView) || (typeof window !== 'undefined' ? window : null);
    if (view && typeof view.matchMedia === 'function') {
      const mq = view.matchMedia('(pointer: coarse)');
      if (mq && mq.matches) return;
    }
    const box = doc ? doc.querySelector(TEXTBOX_SEL) : null;
    if (box && typeof box.focus === 'function') box.focus();
  } catch (e) { /* MM DOM 변동 — 포커스만 못 옮긴다 */ }
}

function frameParts(iframeRef) {
  let frame = null;
  try { frame = iframeRef && iframeRef.current ? iframeRef.current : null; } catch (e) { frame = null; }
  if (!frame) return { win: null, doc: null };
  let win = null;
  let doc = null;
  try { win = frame.contentWindow || null; } catch (e) { win = null; }
  try { doc = frame.contentDocument || (win && win.document) || null; } catch (e) { doc = null; }
  return { win, doc };
}

// 썸네일 값은 «서버가 만든 PNG·GIF·WebP data URL» 일 때만 그린다 — 접두가 다르면 회색 칸으로 둔다
// (그 타일도 클릭하면 원본을 받아 전송은 된다).
function thumbSrc(value) {
  if (typeof value !== 'string') return '';
  for (let i = 0; i < THUMB_PREFIXES.length; i += 1) {
    if (value.indexOf(THUMB_PREFIXES[i]) === 0) return value;
  }
  return '';
}

// 🔴 hasOwnProperty 로 꺼낸다 — 팩 이름 규칙(^[a-z0-9_-]{1,64}$)은 'constructor' 도 통과하는데,
//    그냥 `map[name]` 이면 Object.prototype 의 값이 «이미 받았다» 로 보여 영영 안 받는다.
function ownMap(map, name) {
  return (map && name && Object.prototype.hasOwnProperty.call(map, name)) ? map[name] : null;
}

// 탭 키(팩 이름)로 팩을 찾는다 — 없으면 null(호출부가 첫 팩으로 떨어진다).
function findPack(rows, name) {
  if (!name) return null;
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (row && String(row.pack) === name) return row;
  }
  return null;
}

// 해석된 최근 항목들이 쓰는 팩 이름(중복 제거) — 썸네일은 그 팩마다 1요청이다.
function uniqPackNames(rows) {
  const out = [];
  rows.forEach((row) => {
    const name = (row && row.pack && row.pack.pack) ? String(row.pack.pack) : '';
    if (name && out.indexOf(name) < 0) out.push(name);
  });
  return out;
}

function Credit({ pack }) {
  if (!pack) return null;
  const license = String(pack.license || '');
  const attribution = String(pack.attribution || '');
  if (!license && !attribution) return null;
  // 🔴 hasOwnProperty 로 막는다 — 'toString' 같은 license 문자열이 Object.prototype 의 값을
  //    href 로 끌어오지 못하게(manifest 는 P3 에서 관리자가 편집한다).
  const deed = Object.prototype.hasOwnProperty.call(LICENSE_DEEDS, license) ? LICENSE_DEEDS[license] : '';
  const source = safeHttpUrl(pack.source_url);
  return (
    <p data-testid="gw-sticker-credit"
      style={{ margin: 0, padding: '7px 12px 9px', borderTop: '1px solid #eceae6', fontSize: '11px', color: '#8a8880', lineHeight: 1.5, wordBreak: 'break-all' }}>
      출처:{' '}
      {attribution ? (
        source
          ? <a href={source} target="_blank" rel="noopener noreferrer" style={{ color: '#6b6a66' }}>{attribution}</a>
          : <span>{attribution}</span>
      ) : null}
      {attribution && license ? ' · ' : null}
      {license ? (
        deed
          ? <a href={deed} target="_blank" rel="noopener noreferrer" style={{ color: '#6b6a66' }}>{license}</a>
          : <span>{license}</span>
      ) : null}
    </p>
  );
}

export default function StickerPicker({ open, onClose, iframeRef, anchorRect }) {
  const isMobile = useIsMobile();
  const [packs, setPacks] = useState(manifestCache);
  const [manifestError, setManifestError] = useState(false);
  const [loading, setLoading] = useState(!manifestCache);
  // 🔴 탭은 **문자열 키**다 — ':recent'(최근) 또는 팩 이름. 숫자 인덱스였을 때는 «최근» 이
  //    생기고 사라질 때마다 같은 번호가 다른 팩을 가리켰다.
  const [tab, setTab] = useState('');
  const [recent, setRecent] = useState(NO_RECENT);   // 저장소에서 읽은 `{pack,id}` 목록
  // 🔴 모듈 캐시를 그대로 상태로 쓰지 않는다(얕은 사본) — 같은 객체를 나중에 mutate 하면
  //    React 가 «안 바뀌었다»고 보고 다시 그리지 않는다.
  const [thumbs, setThumbs] = useState(() => Object.assign({}, thumbsCache));
  const [thumbsError, setThumbsError] = useState(false);
  const [thumbsTry, setThumbsTry] = useState(0);       // «다시 시도» 가 올린다(= 재요청 신호)
  const [error, setError] = useState('');
  const [sendingKey, setSendingKey] = useState('');

  const disposedRef = useRef(false);
  const tabInitRef = useRef(false);   // 이번 열림에서 기본 탭을 정했는가(1회)
  const sendingRef = useRef(false);
  const blobsRef = useRef({});        // key -> Blob(전송에 그대로 쓴다)
  const panelRef = useRef(null);
  const channelRef = useRef(null);    // 열 때의 방 스냅샷
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = typeof onClose === 'function' ? onClose : () => {}; }, [onClose]);

  // 지금 보고 있는 탭(훅보다 먼저 정해야 썸네일 effect 가 팩 이름을 볼 수 있다).
  const list = Array.isArray(packs) ? packs : [];
  // 🔴 최근 항목은 **지금 manifest 로 해석**한다 — 빠진 팩·스티커는 버리고, item 은 최신 객체다.
  const recentItems = resolveRecent(recent, list);
  const hasRecent = recentItems.length > 0;
  const isRecentTab = hasRecent && tab === RECENT_TAB;
  // 팩 탭 — 탭 키가 없거나(열자마자) 사라진 팩을 가리키면 첫 팩으로 떨어진다(옛 인덱스 보정과 같은 뜻).
  const pack = isRecentTab ? null : (findPack(list, tab) || list[0] || null);
  const packName = pack && pack.pack ? String(pack.pack) : '';
  // 썸네일이 필요한 팩 — 팩 탭은 그 팩 하나, «최근» 탭은 해석된 항목들의 팩 전부.
  // 🔴 effect 의존성에는 **정렬해 이은 문자열**만 넣는다(배열은 렌더마다 새 참조라 effect 가
  //    매번 다시 돌며 진행 중 요청을 버리고 또 요청한다).
  const needPacks = isRecentTab ? uniqPackNames(recentItems) : (packName ? [packName] : []);
  const needKey = needPacks.slice().sort().join(',');

  // ── 열림/닫힘 정리 ─────────────────────────────────────────────────────────
  // 🔴 disposed 를 세운다 — 늦게 도착한 응답이 닫힌 패널에 setState 하지 않는다.
  useEffect(() => {
    if (!open) return undefined;
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      blobsRef.current = {};
      tabInitRef.current = false;   // 다음에 열 때 기본 탭을 다시 정한다
    };
  }, [open]);

  // g2786 — 모바일 하단 시트가 열려 있는 동안 <html data-gw-sheet="1"> 을 건다 · 닫힘·언마운트 때 지운다.
  //   PushSetupBanner 가 이 표식으로 스스로 숨어 시트 아래 줄을 덮지 않는다(FloatingRecorder 의 gwRecording 과 같은 꼴).
  //   데스크톱 팝오버에는 걸지 않는다(띠가 덮을 하단 시트가 없다).
  useEffect(() => {
    const root = document.documentElement;
    if (open && isMobile) root.dataset.gwSheet = '1';
    else delete root.dataset.gwSheet;
    return () => { delete root.dataset.gwSheet; };
  }, [open, isMobile]);

  // 열 때의 방 스냅샷. 읽다 실패하면 null 로 본다(effect 에서 던져 EmbedPage 트리를 내리지 않는다).
  useEffect(() => {
    if (!open) return undefined;
    channelRef.current = readWinChannel(frameParts(iframeRef).win);
    return undefined;
  }, [open, iframeRef]);

  // manifest — 열 때 1회. 🔴 성공만 캐시한다(오류면 다시 열 때 재시도).
  useEffect(() => {
    if (!open || manifestCache) return undefined;
    let alive = true;
    setLoading(true);
    setManifestError(false);
    stickerAPI.manifest().then(
      (res) => {
        if (!alive) return;
        const rows = res && res.data ? res.data.packs : null;
        manifestCache = Array.isArray(rows) ? rows : [];
        setPacks(manifestCache);
        setLoading(false);
      },
      () => {
        if (!alive) return;
        setManifestError(true);
        setLoading(false);
      },
    );
    return () => { alive = false; };
  }, [open]);

  // «최근» — 열 때 **팩 목록이 처음 준비된 시점에 1회**: 브라우저 저장소를 읽고 기본 탭을 정한다.
  // 🔴 그 뒤로는 덮어쓰지 않는다 — 사용자가 고른 탭도, 전송 직후에도 그대로 둔다(다음에 열 때 다시 정한다).
  // 마지막 탭(10-30): 기억한 탭이 지금도 유효하면 그 탭(«최근» 은 항목이 있을 때만 · 팩은 지금 manifest 에
  //    있을 때만), 아니면 기존 규칙. 🔴 여기서는 writeLastTab 을 부르지 않는다 — 기본값이 저장되면
  //    «한 번도 고른 적 없는 탭» 이 기억으로 남는다. 저장값이 무효여도 고치지 않는다(다음 클릭 때 바뀐다).
  useEffect(() => {
    if (!open || tabInitRef.current || !list.length) return undefined;
    tabInitRef.current = true;
    const rows = readRecent();
    setRecent(rows.length ? rows : NO_RECENT);
    const hasRows = resolveRecent(rows, list).length > 0;
    const last = readLastTab();
    let next = '';
    if (last === RECENT_TAB && hasRows) next = RECENT_TAB;
    else if (last && last !== RECENT_TAB && findPack(list, last)) next = last;
    else next = hasRows ? RECENT_TAB : String((list[0] && list[0].pack) || '');
    setTab(next);
    return undefined;
  }, [open, list.length]);

  // 썸네일 — 지금 탭이 쓰는 **팩마다 요청 1번**. 🔴 성공만 캐시한다(실패를 캐시하면 다시 열어도 영영 회색이다).
  useEffect(() => {
    if (!open || !needKey) return undefined;
    // 팩 이름 규칙(^[a-z0-9_-]{1,64}$)에 ',' 가 없어 키를 그대로 되돌릴 수 있다.
    const names = needKey.split(',');
    const cached = [];
    const missing = [];
    names.forEach((name) => {
      const hit = ownMap(thumbsCache, name);
      if (hit) cached.push([name, hit]);
      else missing.push(name);
    });
    setThumbsError(false);
    if (cached.length) {
      // 이미 받아 둔 팩은 다시 요청하지 않는다 — 상태에만 채운다.
      setThumbs((prev) => {
        const add = cached.filter((row) => !ownMap(prev, row[0]));
        if (!add.length) return prev;
        const next = Object.assign({}, prev);
        add.forEach((row) => { next[row[0]] = row[1]; });
        return next;
      });
    }
    if (!missing.length) return undefined;
    let alive = true;
    missing.forEach((name) => {
      Promise.resolve()
        .then(() => stickerAPI.thumbs(name))
        .then(
          (res) => {
            if (!alive || disposedRef.current) return;
            const data = res && res.data ? res.data.thumbs : null;
            const map = (data && typeof data === 'object') ? data : {};
            thumbsCache[name] = map;
            setThumbs((prev) => Object.assign({}, prev, { [name]: map }));
          },
          () => {
            if (!alive || disposedRef.current) return;
            setThumbsError(true);
          },
        );
    });
    return () => { alive = false; };
  }, [open, needKey, thumbsTry]);

  // Esc·바깥 클릭으로 닫힘.
  // 🔴 iframe 안 이벤트는 부모 문서로 버블링되지 않는다 → 그 문서에도 리스너를 달고, 뗄 때는
  //    **열 때 붙인 doc 참조**를 쓴다(reloadEmbed 로 doc 이 갈릴 수 있다).
  // 🔴 스티커 버튼의 mousedown 은 무시한다 — 안 그러면 mousedown 닫힘 → click 재열림으로 토글이 안 된다.
  useEffect(() => {
    if (!open) return undefined;
    const innerDoc = frameParts(iframeRef).doc;
    const onDown = (ev) => {
      const target = ev && ev.target;
      try {
        if (panelRef.current && target && panelRef.current.contains && panelRef.current.contains(target)) return;
      } catch (e) { /* */ }
      try {
        if (target && target.closest && target.closest('#' + BUTTON_ID)) return;
      } catch (e) { /* */ }
      closeRef.current();
    };
    const onKey = (ev) => {
      if (ev && (ev.key === 'Escape' || ev.keyCode === 27)) closeRef.current();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    if (innerDoc) {
      try {
        innerDoc.addEventListener('mousedown', onDown, true);
        innerDoc.addEventListener('keydown', onKey, true);
      } catch (e) { /* */ }
    }
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      if (innerDoc) {
        try {
          innerDoc.removeEventListener('mousedown', onDown, true);
          innerDoc.removeEventListener('keydown', onKey, true);
        } catch (e) { /* */ }
      }
    };
  }, [open, iframeRef]);

  // 🔴 원본(618px)은 **여기서만** 받는다 — 클릭한 한 장, 재시도 없이 1회. 그리드는 썸네일 묶음이 채운다.
  function ensureBlob(pickPack, item) {
    const key = tileKey(pickPack, item.id);
    const have = blobsRef.current[key];
    if (have) return Promise.resolve(have);
    return Promise.resolve()
      .then(() => stickerAPI.image(pickPack, item.id, item.v))
      .then(
        (res) => {
          const data = res && res.data;
          if (!data) throw new StickerSendError(0, MSG_IMAGE_FAILED);
          blobsRef.current[key] = data;
          return data;
        },
        (err) => {
          if (err instanceof StickerSendError) throw err;
          throw new StickerSendError(statusOf(err), MSG_IMAGE_FAILED);
        },
      );
  }

  // 클릭 = 입력창 위 첨부 대기열에 올린다(stageSticker) · 게시는 사용자가 엔터/전송으로 한다.
  // 🔴 클릭 흐름 전체를 try/catch/finally 로 감싼다 — 스냅샷 재파싱·iframe null·Blob 받기·올리기
  //    어디서 터져도 패널만 문구를 띄우고, EmbedPage 트리는 그대로 산다.
  async function handlePick(pack, item) {
    if (sendingRef.current || disposedRef.current) return;   // 처리 중 다른 클릭은 무시(이중 처리 방지)
    sendingRef.current = true;
    setSendingKey(tileKey(pack.pack, item.id));
    setError('');
    try {
      const parts = frameParts(iframeRef);
      if (!parts.win || !parts.doc) throw new StickerSendError(0, MSG_NO_FRAME);
      if (!sameChannel(channelRef.current, readWinChannel(parts.win))) {
        if (!disposedRef.current) setError(MSG_CHANNEL_CHANGED);
        return;
      }
      const blob = await ensureBlob(pack.pack, item);
      const ext = String(item.ext || 'png');
      await stageSticker({
        doc: parts.doc,
        blob,
        filename: 'sticker_' + pack.pack + '_' + item.id + '.' + ext,
      });
      // 🔴 «최근» 기록은 입력창에 **올린 뒤** 여기서만 한다(피커는 그 뒤 엔터/전송을 알 수 없다 — 의도된
      //    시점 변경) — disposed 검사 밖이다(패널이 이미 닫혔어도 올린 건 올린 것이다).
      //    실패·방 변경·처리 중 무시 경로는 여기까지 오지 않는다.
      pushRecent(pack.pack, item.id);
      if (!disposedRef.current) {
        closeRef.current();
        focusTextbox(parts.doc);
      }
    } catch (e) {
      if (!disposedRef.current) setError(e instanceof StickerSendError ? e.userMessage : MSG_STAGE_FAILED);
    } finally {
      sendingRef.current = false;
      if (!disposedRef.current) setSendingKey('');
    }
  }

  // 탭 클릭 — 🔴 마지막 탭은 **사용자가 누를 때만** 기록한다(기본 탭 effect·전송 경로는 쓰지 않는다).
  const pickTab = (key) => {
    setTab(key);
    writeLastTab(key);
  };

  if (!open) return null;

  const stickers = (pack && Array.isArray(pack.stickers)) ? pack.stickers : [];
  // 타일은 «팩 객체 + 스티커» 쌍으로 다룬다 — «최근» 탭은 팩이 섞일 수 있다.
  const tiles = isRecentTab ? recentItems : stickers.map((item) => ({ pack, item }));
  // 탭 줄 — «최근» 이 있으면 맨 앞, 그 뒤로 팩이 manifest 순서대로.
  const tabs = hasRecent ? [{ key: RECENT_TAB, label: MSG_RECENT }] : [];
  list.forEach((p) => { if (p) tabs.push({ key: String(p.pack), label: p.label || p.pack }); });
  const activeTab = isRecentTab ? RECENT_TAB : packName;

  // 데스크톱: 버튼 위쪽에 띄우고 화면 밖이면 안쪽으로 당긴다(anchorRect 는 iframe 문서 px).
  // 🔴 웹 배율(<html> zoom)이면 iframe rect·innerWidth/innerHeight 는 화면 px 이고 left/top 은 CSS px 다 →
  //    iframe 의 rect 폭 ÷ offsetWidth(scaleOf · 배율 없음·jsdom 은 1)로 나눠 CSS px 로 맞춘다.
  //    anchor 는 나누지 않는다 — 부모 zoom 이 iframe 렌더도 같이 키워 부모 CSS px 와 같다(Chrome 추정 · 미실측).
  // 🔴 모바일은 이 계산을 쓰지 않는다 — 좌표 무관 하단 시트로 간다(아래 shell).
  function desktopBox() {
    let frameRect = { left: 0, top: 0 };
    let s = 1;
    try {
      const frame = iframeRef && iframeRef.current;
      const rect = frame && frame.getBoundingClientRect ? frame.getBoundingClientRect() : null;
      if (rect) {
        frameRect = rect;
        s = scaleOf(Number(rect.width) || 0, Number(frame.offsetWidth) || 0);
      }
    } catch (e) { /* */ }
    const anchor = anchorRect || {};
    const anchorLeft = (Number(frameRect.left) || 0) / s + (Number(anchor.left) || 0);
    const anchorTop = (Number(frameRect.top) || 0) / s + (Number(anchor.top) || 0);
    const width = Number(anchor.width) || 0;
    const viewW = (typeof window !== 'undefined' && window.innerWidth) ? window.innerWidth / s : PANEL_W + EDGE * 2;
    const viewH = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight / s : PANEL_H + EDGE * 2;
    let left = anchorLeft + width / 2 - PANEL_W / 2;
    let top = anchorTop - PANEL_H - EDGE;
    left = Math.max(EDGE, Math.min(left, Math.max(EDGE, viewW - PANEL_W - EDGE)));
    top = Math.max(EDGE, Math.min(top, Math.max(EDGE, viewH - PANEL_H - EDGE)));
    return { left, top };
  }

  const box = isMobile ? null : desktopBox();
  const shell = isMobile
    ? { position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '70vh', borderRadius: '14px 14px 0 0' }
    : { position: 'fixed', left: box.left + 'px', top: box.top + 'px', width: PANEL_W + 'px', height: PANEL_H + 'px', borderRadius: '12px' };

  const tabStyle = (on) => ({
    padding: '7px 12px', fontSize: '12.5px', fontWeight: on ? 700 : 500, whiteSpace: 'nowrap',
    color: on ? '#c6613f' : '#6b6a66', background: 'none', border: 'none',
    borderBottom: '2px solid ' + (on ? '#c6613f' : 'transparent'), cursor: 'pointer',
  });

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="스티커"
      data-testid="gw-sticker-picker"
      className={isMobile ? 'gw-tabbar-pad' : undefined}
      style={Object.assign({
        zIndex: 2147483601, background: '#fff', boxShadow: '0 10px 40px rgba(0,0,0,.22)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box',
        fontFamily: 'var(--gw-font)',
      }, shell)}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px 4px' }}>
        <b style={{ fontSize: '13.5px', color: '#1f1e1c' }}>스티커</b>
        <button type="button" onClick={() => closeRef.current()} aria-label="닫기"
          style={{ background: 'none', border: 'none', fontSize: '17px', lineHeight: 1, color: '#8a8880', cursor: 'pointer', padding: '0 2px' }}>×</button>
      </div>
      {tabs.length > 1 && (
        <div style={{ display: 'flex', gap: '2px', overflowX: 'auto', borderBottom: '1px solid #eceae6', padding: '0 6px' }}>
          {tabs.map((t) => (
            <button key={t.key} type="button" style={tabStyle(t.key === activeTab)} aria-pressed={t.key === activeTab}
              onClick={() => pickTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignContent: 'flex-start' }}>
        {loading && <span style={{ fontSize: '12.5px', color: '#8a8880' }}>불러오는 중…</span>}
        {!loading && manifestError && (
          <span data-testid="gw-sticker-manifest-error" style={{ fontSize: '12.5px', color: '#b91c1c' }}>{MSG_MANIFEST_ERR}</span>
        )}
        {!loading && !manifestError && !tiles.length && (
          <span style={{ fontSize: '12.5px', color: '#8a8880' }}>{MSG_EMPTY}</span>
        )}
        {!loading && !manifestError && thumbsError && (
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span data-testid="gw-sticker-thumbs-error" style={{ fontSize: '12.5px', color: '#b91c1c' }}>{MSG_MANIFEST_ERR}</span>
            <button type="button" onClick={() => setThumbsTry((n) => n + 1)}
              style={{ background: 'none', border: '1px solid #eceae6', borderRadius: '7px', padding: '3px 8px', fontSize: '12px', color: '#6b6a66', cursor: 'pointer' }}>
              {MSG_RETRY}
            </button>
          </div>
        )}
        {!loading && !manifestError && tiles.map((tile) => {
          const tilePack = tile.pack;
          const item = tile.item;
          const key = tileKey(tilePack.pack, item.id);
          // 🔴 썸네일에 없는 id 는 «회색 칸» — 글자도 다른 동작도 없다. 클릭하면 원본을 받아 그대로 보낸다.
          const src = thumbSrc(ownMap(ownMap(thumbs, String(tilePack.pack)), item.id));
          const label = item.label || item.id;
          return (
            <button
              key={key}
              type="button"
              data-gw-sticker-key={key}
              data-gw-sticker-pack={tilePack.pack}
              data-gw-sticker-id={item.id}
              data-gw-sticker-v={item.v === undefined || item.v === null ? '' : String(item.v)}
              disabled={!!sendingKey}
              aria-label={label}
              title={label}
              onClick={() => handlePick(tilePack, item)}
              style={{
                width: TILE + 'px', height: TILE + 'px', padding: 0, borderRadius: '9px',
                border: '1px solid ' + (sendingKey === key ? '#c6613f' : '#eceae6'),
                background: src ? '#fff' : '#f3f2ef',
                cursor: sendingKey ? 'default' : 'pointer', overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '10.5px', color: '#8a8880',
              }}
            >
              {src
                ? <img src={src} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }} />
                : <span />}
            </button>
          );
        })}
      </div>
      {error ? (
        <p role="alert" data-testid="gw-sticker-error"
          style={{ margin: 0, padding: '7px 12px', borderTop: '1px solid #eceae6', fontSize: '12px', color: '#b91c1c', lineHeight: 1.5 }}>
          {error}
        </p>
      ) : null}
      {/* 출처 줄은 **팩 탭에서만** 그린다 — «최근» 은 팩이 섞여 한 줄로 묶을 수 없다(각 팩 탭에 그대로 있다). */}
      <Credit pack={isRecentTab ? null : pack} />
    </div>
  );

  if (!isMobile) return panel;
  // 모바일 하단 시트 — 딤 배경은 «바깥»이라 document mousedown 리스너가 그대로 닫는다.
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2147483600, background: 'rgba(0,0,0,.35)' }}>
      {panel}
    </div>
  );
}
